import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useWebSocket from 'react-use-websocket';
import { useDispatch } from 'react-redux';
import { setLatestPayload } from '@/store/slices/ReportsSlice'
import { reportsApi } from '@/api/rtk/reportsApi'
import { patchReportsListCaches } from '@/api/rtk/patchReportsListCaches'
import { useCurrentTool } from '@/shared/hooks/useCurrentTool'
import { fetchAuthSessionWithLogout } from '@/features/auth/lib/authSession';

const WS_URL = import.meta.env.VITE_WS_URL

export function useReportStatusSocket(enabled) {
  const dispatch = useDispatch();
  const { toolId, toolName, currentToolFields, getToolDescription, tGeneration } = useCurrentTool();
  const [socketUrl, setSocketUrl] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setSocketUrl(null);
      return;
    }

    let cancelled = false;
    const resolveUrl = async () => {
      const accessToken = await fetchAuthSessionWithLogout();
      if (!cancelled && accessToken && WS_URL) {
        setSocketUrl(`${WS_URL}?token=${encodeURIComponent(accessToken.toString())}`);
      }
    };
    resolveUrl();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const { lastJsonMessage } = useWebSocket(socketUrl, {
    shouldReconnect: () => !!enabled,
    onClose: () => {
      if (!enabled) return;
      fetchAuthSessionWithLogout().then((accessToken) => {
        if (!accessToken || !WS_URL) {
          setSocketUrl(null);
          return;
        }
        const nextUrl = `${WS_URL}?token=${encodeURIComponent(accessToken.toString())}`;
        setSocketUrl((prev) => (prev === nextUrl ? prev : nextUrl));
      });
    },
    heartbeat: {
      message: 'ping',
      returnMessage: 'pong',
      timeout: 5 * 60000,
      interval: 5 * 1000,
    },
  });

  useEffect(() => {
    if (!lastJsonMessage) return;

    const reportId = lastJsonMessage.data?.report_id || lastJsonMessage.data?.id;
    if (!reportId) return;

    let cancelled = false;

    const applyNotification = async () => {
      try {
        const result = await dispatch(
          reportsApi.endpoints.getReport.initiate(
            { id: reportId, currentToolFields },
            { forceRefetch: true, subscribe: false }
          )
        ).unwrap();
        const report = result?.message?.[0];
        if (cancelled || !report) return;

        // Keep an open report detail in sync even if the user is on another tool.
        dispatch(reportsApi.util.updateQueryData(
          'getReport',
          { id: reportId, currentToolFields },
          (draft) => {
            if (draft?.message?.[0]) Object.assign(draft.message[0], report);
          }
        ));

        // List / toast / latestPayload belong to the active tool page only.
        if (String(toolId) !== String(report.tool_id)) return;

        dispatch(setLatestPayload(report));
        dispatch(patchReportsListCaches(report, { toolName, currentToolFields }));

        if (report.status === "completed" && !report.report_fetched) {
          const errorMessage = report.error === "no_transcription_found"
            ? tGeneration("form.errors.transcriptionFailed")
            : tGeneration("form.errors.generationFailed");

          toast.error(`${getToolDescription(report.tool_id)} #${report.row_number}: ${errorMessage}`, { toastId: `toast-${report.tool_id}` });
        }
      } catch (error) {
        console.error('Failed to refresh report after websocket update:', error);
      }
    };

    applyNotification();
    return () => {
      cancelled = true;
    };
  }, [dispatch, lastJsonMessage, toolId, toolName, currentToolFields, tGeneration, getToolDescription]);
}
